import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../lib/auth';
import DashboardLayout from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { User, Shield, Building2, Bell, Clock, MessageSquare, Mail, Send, CheckCircle, AlertCircle, Zap, RefreshCw, Sparkles } from 'lucide-react';
import api from '../lib/api';
import analytics from '../lib/tracking';
import { toast } from 'sonner';

function UpgradeCard() {
  const [couponCode, setCouponCode] = useState('');
  const [couponResult, setCouponResult] = useState(null);
  const [selectedPkg, setSelectedPkg] = useState('growth_monthly');
  return (
    <div className="p-4 rounded-xl bg-gradient-to-r from-[#FFF8F5] to-[#FFF2EA] border border-[#FF4500]/10">
      <div className="flex items-center gap-2 mb-3"><Sparkles className="w-4 h-4 text-[#FF4500]" /><p className="text-sm font-bold text-[#0B0F17]">Upgrade your plan</p></div>
      <div className="mb-3">
        <Select value={selectedPkg} onValueChange={(v) => { setSelectedPkg(v); setCouponResult(null); }}>
          <SelectTrigger className="h-9 text-xs bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="growth_monthly">Growth — $49 USD/month</SelectItem>
            <SelectItem value="growth_annual">Growth — $490 USD/year</SelectItem>
            <SelectItem value="agency_monthly">Agency — $99 USD/month</SelectItem>
            <SelectItem value="agency_annual">Agency — $990 USD/year</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex gap-2 mb-3">
        <Input placeholder="Coupon code" value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} className="h-8 text-xs font-mono flex-1 bg-white" />
        <Button variant="outline" size="sm" className="h-8 text-[10px]"
          onClick={async () => { try { const r = await api.validateCoupon(couponCode, selectedPkg); setCouponResult(r); analytics.couponApplied(couponCode, r.discount_amount); toast.success(`${r.discount_type === 'percentage' ? r.discount_value + '% off' : '$' + r.discount_value + ' off'} applied!`); } catch(e) { analytics.couponFailed(couponCode, e.message); toast.error(e.message); setCouponResult(null); } }}>
          Apply
        </Button>
      </div>
      {couponResult && (
        <div className="text-xs mb-3 p-2 rounded-lg bg-white border border-[#E6E8EE]">
          <span className="text-[#16A34A] font-semibold">{couponResult.code}</span> — <span className="line-through text-[#9ca3af] mx-1">${couponResult.original_amount}</span> <span className="font-bold text-[#0B0F17]">${couponResult.final_amount}</span>
        </div>
      )}
      <Button className="h-9 w-full rounded-xl text-xs font-bold cta-btn shadow-md shadow-[#FF4500]/20" style={{ background: 'linear-gradient(135deg, #FF6A2A 0%, #FF4500 100%)' }}
        onClick={async () => { try { analytics.checkoutStarted(selectedPkg, couponResult ? couponResult.final_amount : 49, couponResult ? couponCode : null); const r = await api.createCheckout(selectedPkg, couponResult ? couponCode : null); window.location.href = r.url; } catch(e) { toast.error(e.message); } }}>
        {couponResult ? `Pay $${couponResult.final_amount}` : 'Upgrade Now'}
      </Button>
    </div>
  );
}

const fadeUp = {
  hidden: { opacity: 0, y: 15 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.4, ease: [0.22, 1, 0.36, 1] } })
};

export default function Settings() {
  const { user, activeBusiness, businesses } = useAuth();
  const [notifSettings, setNotifSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingSlack, setTestingSlack] = useState(false);
  const [scanLogs, setScanLogs] = useState([]);

  // Local form state
  const [slackUrl, setSlackUrl] = useState('');
  const [emailNotif, setEmailNotif] = useState(false);
  const [notifEmail, setNotifEmail] = useState('');
  const [threshold, setThreshold] = useState(70);
  const [autoScan, setAutoScan] = useState(true);
  const [scanInterval, setScanInterval] = useState('30');

  const fetchSettings = useCallback(async () => {
    if (!activeBusiness) return;
    setLoading(true);
    try {
      const [settings, logs] = await Promise.all([
        api.getNotificationSettings(activeBusiness._id),
        api.getScanLogs(activeBusiness._id, 10),
      ]);
      setNotifSettings(settings);
      setSlackUrl(settings.slack_webhook_url || '');
      setEmailNotif(settings.email_notifications || false);
      setNotifEmail(settings.notification_email || '');
      setThreshold(settings.intent_threshold || 70);
      setAutoScan(settings.auto_scan_enabled !== false);
      setScanInterval(String(settings.scan_interval_minutes || 30));
      setScanLogs(logs || []);
    } catch {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [activeBusiness]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    if (!activeBusiness) return;
    setSaving(true);
    try {
      await api.updateNotificationSettings(activeBusiness._id, {
        slack_webhook_url: slackUrl,
        email_notifications: emailNotif,
        notification_email: notifEmail,
        intent_threshold: threshold,
        auto_scan_enabled: autoScan,
        scan_interval_minutes: parseInt(scanInterval),
      });
      toast.success('Settings saved!');
      analytics.settingsSaved('notifications');
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSlack = async () => {
    if (!activeBusiness) return;
    setTestingSlack(true);
    try {
      await api.testSlackNotification(activeBusiness._id);
      toast.success('Test notification sent to Slack!');
    } catch (err) {
      toast.error(err.message || 'Failed to send test notification');
    } finally {
      setTestingSlack(false);
    }
  };

  return (
    <DashboardLayout title="Settings">
      <div className="max-w-3xl space-y-6">

        {/* Account */}
        <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}>
          <Card className="p-6 rounded-2xl border-[#E6E8EE] bg-white shadow-[0_1px_3px_rgba(16,24,40,0.04)]">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-xl bg-[#FFF2EA] flex items-center justify-center">
                <User className="w-4 h-4 text-[#FF4500]" />
              </div>
              <h3 className="text-base font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Account</h3>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-[#E6E8EE]">
                <span className="text-sm text-[#5B667A]">Email</span>
                <span className="text-sm font-medium">{user?.email}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-[#E6E8EE]">
                <span className="text-sm text-[#5B667A]">Role</span>
                <Badge variant="secondary" className="text-xs capitalize">{user?.role}</Badge>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-[#5B667A]">Businesses</span>
                <span className="text-sm font-medium">{businesses.length}</span>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Auto-Scanning Settings */}
        <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={1}>
          <Card className="p-6 rounded-2xl border-[#E6E8EE] bg-white shadow-[0_1px_3px_rgba(16,24,40,0.04)]">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-xl bg-[#EFF6FF] flex items-center justify-center">
                <RefreshCw className="w-4 h-4 text-[#2563EB]" />
              </div>
              <div>
                <h3 className="text-base font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Background Scanning</h3>
                <p className="text-xs text-[#5B667A]">Automatically scan Reddit for new leads on a schedule</p>
              </div>
            </div>

            {loading ? (
              <div className="space-y-4"><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /></div>
            ) : (
              <div className="space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-[#0B0F17]">Enable auto-scanning</p>
                    <p className="text-xs text-[#5B667A]">Periodically scan subreddits for new leads</p>
                  </div>
                  <Switch checked={autoScan} onCheckedChange={setAutoScan} data-testid="auto-scan-toggle" />
                </div>

                {autoScan && (
                  <div>
                    <label className="text-sm font-medium text-[#0B0F17] block mb-2">Scan interval</label>
                    <Select value={scanInterval} onValueChange={setScanInterval}>
                      <SelectTrigger className="w-full h-10 rounded-lg" data-testid="scan-interval-select">
                        <SelectValue placeholder="Select interval" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">Every 5 minutes</SelectItem>
                        <SelectItem value="15">Every 15 minutes</SelectItem>
                        <SelectItem value="30">Every 30 minutes</SelectItem>
                        <SelectItem value="60">Every hour</SelectItem>
                        <SelectItem value="360">Every 6 hours</SelectItem>
                        <SelectItem value="720">Every 12 hours</SelectItem>
                        <SelectItem value="1440">Once a day</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {notifSettings?.last_auto_scan_iso && (
                  <div className="flex items-center gap-2 text-xs text-[#5B667A] bg-[#F6F7F9] px-3 py-2 rounded-lg">
                    <Clock className="w-3.5 h-3.5" />
                    Last auto-scan: {new Date(notifSettings.last_auto_scan_iso).toLocaleString()}
                  </div>
                )}
              </div>
            )}
          </Card>
        </motion.div>

        {/* Notification Settings */}
        <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={2}>
          <Card className="p-6 rounded-2xl border-[#E6E8EE] bg-white shadow-[0_1px_3px_rgba(16,24,40,0.04)]">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-xl bg-[#FFF2EA] flex items-center justify-center">
                <Bell className="w-4 h-4 text-[#FF4500]" />
              </div>
              <div>
                <h3 className="text-base font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Notifications</h3>
                <p className="text-xs text-[#5B667A]">Get notified when high-intent leads are discovered</p>
              </div>
            </div>

            {loading ? (
              <div className="space-y-4"><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /></div>
            ) : (
              <div className="space-y-6">
                {/* Intent threshold */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-[#0B0F17]">Intent score threshold</label>
                    <span className="text-sm font-bold text-[#FF4500] tabular-nums" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{threshold}</span>
                  </div>
                  <p className="text-xs text-[#5B667A] mb-3">Only notify for leads with intent score at or above this value</p>
                  <Slider
                    value={[threshold]}
                    onValueChange={(val) => setThreshold(val[0])}
                    min={0}
                    max={100}
                    step={5}
                    className="w-full"
                    data-testid="intent-threshold-slider"
                  />
                  <div className="flex justify-between text-[10px] text-[#9ca3af] mt-1">
                    <span>0 (all leads)</span>
                    <span>50</span>
                    <span>100 (only highest)</span>
                  </div>
                </div>

                <div className="border-t border-[#E6E8EE]" />

                {/* Slack */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <MessageSquare className="w-4 h-4 text-[#5B667A]" />
                    <label className="text-sm font-medium text-[#0B0F17]">Slack notifications</label>
                  </div>
                  <p className="text-xs text-[#5B667A] mb-3">
                    Paste your Slack incoming webhook URL. <a href="https://api.slack.com/messaging/webhooks" target="_blank" rel="noopener noreferrer" className="text-[#FF4500] hover:underline">How to create a webhook</a>
                  </p>
                  <div className="flex gap-2">
                    <Input
                      placeholder="https://hooks.slack.com/services/..."
                      value={slackUrl}
                      onChange={(e) => setSlackUrl(e.target.value)}
                      className="h-10 rounded-lg flex-1 text-sm"
                      data-testid="slack-webhook-input"
                    />
                    {slackUrl && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-10 rounded-lg text-xs whitespace-nowrap"
                        onClick={handleTestSlack}
                        disabled={testingSlack}
                        data-testid="test-slack-button"
                      >
                        {testingSlack ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
                        Test
                      </Button>
                    )}
                  </div>
                </div>

                <div className="border-t border-[#E6E8EE]" />

                {/* Email */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-[#5B667A]" />
                      <label className="text-sm font-medium text-[#0B0F17]">Email notifications</label>
                    </div>
                    <Switch checked={emailNotif} onCheckedChange={setEmailNotif} data-testid="email-notif-toggle" />
                  </div>
                  {emailNotif && (
                    <div>
                      <p className="text-xs text-[#5B667A] mb-2">Notification email address</p>
                      <Input
                        type="email"
                        placeholder="you@company.com"
                        value={notifEmail}
                        onChange={(e) => setNotifEmail(e.target.value)}
                        className="h-10 rounded-lg text-sm"
                        data-testid="notification-email-input"
                      />
                      <p className="text-[10px] text-[#9ca3af] mt-1.5">Requires SMTP configuration on the server.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Card>
        </motion.div>

        {/* Save Button */}
        <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={3}>
          <Button
            onClick={handleSave}
            disabled={saving || loading}
            className="h-11 px-8 rounded-xl font-bold text-sm cta-btn shadow-lg"
            style={{ background: 'linear-gradient(135deg, #FF6A2A 0%, #FF4500 100%)' }}
            data-testid="save-settings-button"
          >
            {saving ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : 'Save Settings'}
          </Button>
        </motion.div>

        {/* Scan History */}
        {scanLogs.length > 0 && (
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={4}>
            <Card className="p-6 rounded-2xl border-[#E6E8EE] bg-white shadow-[0_1px_3px_rgba(16,24,40,0.04)]">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-xl bg-[#F0FDF4] flex items-center justify-center">
                  <Clock className="w-4 h-4 text-[#16A34A]" />
                </div>
                <h3 className="text-base font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Recent Scan History</h3>
              </div>
              <div className="space-y-2">
                {scanLogs.map((log, i) => (
                  <div key={log._id || i} className="flex items-center justify-between py-2.5 border-b border-[#E6E8EE] last:border-b-0">
                    <div className="flex items-center gap-3">
                      {log.new_leads > 0 ? (
                        <CheckCircle className="w-4 h-4 text-[#16A34A]" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-[#9ca3af]" />
                      )}
                      <div>
                        <p className="text-sm text-[#0B0F17]">
                          {log.new_leads > 0
                            ? `${log.new_leads} new leads (${log.high_intent_leads || 0} high-intent)`
                            : 'No new leads found'
                          }
                        </p>
                        <p className="text-[11px] text-[#9ca3af]">{log.posts_scanned} posts scanned</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {log.notifications_sent && (
                        <Badge variant="secondary" className="text-[9px] bg-[#FFF2EA] text-[#FF4500]">
                          <Bell className="w-2.5 h-2.5 mr-0.5" /> Notified
                        </Badge>
                      )}
                      <span className="text-[11px] text-[#9ca3af]">
                        {log.timestamp ? new Date(log.timestamp).toLocaleString() : ''}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </motion.div>
        )}

        {/* Plan & Upgrade */}
        <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={5}>
          {(() => {
            const p = user?.plan || 'free';
            const planLabel = p === 'agency' ? 'Agency' : p === 'pro' ? 'Growth' : 'Startup';
            const planBg = p === 'free' ? '#FFF2EA' : '#E9F9EF';
            const planFg = p === 'free' ? '#FF4500' : '#166534';
            const limits = p === 'agency' ? { biz: 10, kw: 50, sub: 50, leads: 'Unlimited', ai: 500, scan: 'Every 30 min' }
              : p === 'pro' ? { biz: 2, kw: 50, sub: 50, leads: 'Unlimited', ai: 150, scan: 'Every 30 min' }
              : { biz: 1, kw: 5, sub: 5, leads: '3/day', ai: '0 (paid only)', scan: 'Once daily' };
            return (
              <Card className={`p-6 rounded-2xl border-[#E6E8EE] bg-white shadow-[0_1px_3px_rgba(16,24,40,0.04)] ${p === 'free' ? 'ring-1 ring-[#FF4500]/20' : ''}`}>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 rounded-xl bg-[#FFF2EA] flex items-center justify-center"><Shield className="w-4 h-4 text-[#FF4500]" /></div>
                  <div>
                    <h3 className="text-base font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Plan & Limits</h3>
                    <p className="text-xs text-[#5B667A]">Current plan: <Badge className="text-[10px] ml-1" style={{ background: planBg, color: planFg }}>{planLabel}</Badge></p>
                  </div>
                </div>
                <div className="space-y-3 mb-5">
                  {[
                    ['Businesses', limits.biz], ['Keywords / Business', limits.kw], ['Subreddits / Business', limits.sub],
                    ['Leads Visible / Day', limits.leads], ['AI Replies / Month', limits.ai], ['Scan Frequency', limits.scan],
                  ].map(([label, val], i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-[#E6E8EE] last:border-b-0">
                      <span className="text-sm text-[#5B667A]">{label}</span>
                      <span className="text-sm font-medium">{val}</span>
                    </div>
                  ))}
                </div>
                {p === 'free' && <UpgradeCard />}
              </Card>
            );
          })()}
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
